import { ReactNode, useState } from 'react';
import { Steps } from 'antd';
import { Button, SIZE, Stack } from '@quinyx/ui';

import styles from './index.module.scss';
import { useTranslate } from '../../hooks/translate';

const { Step } = Steps;

export type NextCallback = (
  step: number,
  setStepStatusError: (description: string) => void,
) => Promise<boolean>;

type Props = {
  steps: StepContent[];
  nextCallback: NextCallback;
};

type StepContent = {
  title: string;
  content: ReactNode;
  nextLabel?: string;
};

type StepStatus = {
  description: string;
  status: 'wait' | 'process' | 'finish' | 'error';
};

const StepsComponent = ({ steps, nextCallback }: Props) => {
  const { t } = useTranslate();
  const [current, setCurrent] = useState(0);
  const [stepStatus, setStepStatus] = useState<StepStatus[]>([]);

  const setStepStatusError = (description: string) => {
    setStepStatus((currentStepStatus) => {
      const stepStatusCopy = currentStepStatus;
      stepStatusCopy[current] = {
        description,
        status: 'error',
      };
      return stepStatusCopy;
    });
  };

  const next = () => async () => {
    const ok = await nextCallback(current, setStepStatusError);
    if (current === steps.length - 1) {
      setStepStatus([]);
      setCurrent(0);
    } else if (ok) {
      setCurrent(current + 1);
    }
  };

  const prev = () => {
    setStepStatus([]);
    setCurrent(current - 1);
  };

  return (
    <>
      <Steps current={current}>
        {steps.map((item, index) => (
          <Step
            key={item.title}
            title={item.title}
            status={stepStatus[index]?.status}
            description={stepStatus[index]?.description}
          />
        ))}
      </Steps>
      <div className={styles.stepsContent}>{steps[current].content}</div>
      <Stack direction="row" spacing={SIZE.S} mt={SIZE.L}>
        {current > 0 && (
          <Button
            data-test-id="previous-button"
            onClick={() => prev()}
            text={t('common.previous')}
            variant="text"
          />
        )}
        {current < steps.length - 1 && (
          <Button
            data-test-id="next-button"
            type="submit"
            onClick={next()}
            text={steps[current].nextLabel ?? t('common.next')}
          />
        )}
        {current === steps.length - 1 && (
          <Button
            data-test-id="done-button"
            type="submit"
            onClick={next()}
            text={t('common.done')}
          />
        )}
      </Stack>
    </>
  );
};

export default StepsComponent;
